import { Field } from './Field';
import { controlClasses } from './fieldStyles';

/**
 * A single-line text control inside the standard field frame.
 *
 * `required` sets `aria-required` but deliberately not the HTML `required`
 * attribute: native constraint validation would block submission before the
 * shared Zod schema runs and show a browser bubble that is not associated with
 * the field, not styled with the site, and not announced consistently. The form
 * carries `noValidate` for the same reason. Validation is the shared schema's
 * job, in the browser and again on the server (spec section 14).
 */
export interface TextFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
  required?: boolean;
  type?: 'text' | 'email' | 'tel' | 'url';
  /** Mobile keyboard hint. `numeric` for the ZIP fields. */
  inputMode?: 'text' | 'numeric' | 'email' | 'tel' | 'url';
  autoComplete?: string;
  maxLength?: number;
}

export function TextField({
  id,
  name,
  label,
  value,
  onChange,
  hint,
  error,
  required = false,
  type = 'text',
  inputMode,
  autoComplete,
  maxLength,
}: TextFieldProps) {
  return (
    <Field id={id} label={label} hint={hint} error={error} optional={!required}>
      {({ id: controlId, describedBy, invalid }) => (
        <input
          id={controlId}
          name={name}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          maxLength={maxLength}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={controlClasses(invalid)}
        />
      )}
    </Field>
  );
}
